import React, {useEffect, useState} from "react";
import {FarmyButton, FarmyCloseIcon} from "../../react-components/shared/SharedComponents";
import {useSelector} from "../../shared-services/redux-tiny";
import useCart, {cartTools} from "../cart/useCart";
import {cartParserTools} from "../cart/cartParser";
import {t} from "../../shared-services/translate";

const useCartButtons = (passedProduct = null, options = {}) => {
  const {currentCart} = useSelector(state => state.cart);
  const {lineItemFromProduct, isLastVariant: checkIfLastVariant} = cartTools();
  const {addToCart, increaseQuantity, decreaseQuantity, removeFromCart} = useCart();

  const [lineItem, setLineItem] = useState(null);
  const [isLastVariant, setIsLastVariant] = useState(false);
  const [parsedQuantity, setParsedQuantity] = useState({});

  const {splitLabel} = cartParserTools();

  const onAddToCartClick = (product = passedProduct) => {
    addToCart(product, options);
    if (options.onAddToCart) options.onAddToCart();
  };

  const onRemoveClick = (product = passedProduct) => {
    removeFromCart(product, options);
    if (options.onRemove) options.onRemove();
  };

  const onIncreaseClick = (product = passedProduct) => {
    increaseQuantity(product, options);
    if (options.onIncrease) options.onIncrease();
  };

  useEffect(() => {
    if (!passedProduct?.id) return;

    const newLineItem = lineItemFromProduct(passedProduct);

    setLineItem(newLineItem);
    setIsLastVariant(checkIfLastVariant(passedProduct));
    
    if (newLineItem) {
      const quantityLabel = newLineItem?.variant?.quantity_label || newLineItem?.variant?.label;
      setParsedQuantity(splitLabel(quantityLabel));
    }
  }, [currentCart, passedProduct]);

  const AddToCartButton = (props) => {
    const {
      product = passedProduct,
      children,
      size = "lg",
      color = "orange",
      className = null,
      classNameDisabled = null,
      style,
      ...additionalProps
    } = props;
    const hasChildren = children ? children.length > 0 : false;

    return <FarmyButton
      disabled={!product?.available_to_add_to_cart}
      size={size}
      color={color}
      className={
        product?.available_to_add_to_cart
          ? className
          : classNameDisabled || className
      }
      style={style}
      onClick={() => onAddToCartClick(product)}
      {...additionalProps}
    >
      {hasChildren
        ? children
        : t(product?.available_to_add_to_cart
          ? "product_item.cart.add_to_cart"
          : "products.unavailable"
        )
      }
    </FarmyButton>;
  };

  const RemoveButton = (props) => {
    const {
      product = passedProduct,
      children,
      size = "lg",
      color = "orange",
      iconColor = "black",
      className = "",
      style,
      ...additionalProps
    } = props;
    const hasChildren = children ? children.length > 0 : false;

    return <FarmyButton
      size={size}
      color={color}
      className={className}
      style={style}
      onClick={() => onRemoveClick(product)}
      {...additionalProps}
    >
      {hasChildren ? children : <FarmyCloseIcon style={{opacity: 0.5}} color={iconColor}/>}
    </FarmyButton>;
  };

  const DecreaseButton = (props) => {
    const {
      product = passedProduct,
      children,
      size = "lg",
      color = "orange",
      className = "",
      style,
      ...additionalProps
    } = props;
    const hasChildren = !!children;

    return <FarmyButton
      size={size}
      color={color}
      className={className}
      style={style}
      onClick={() => decreaseQuantity(product, options)}
      {...additionalProps}
    >
      {hasChildren ? children : "-"}
    </FarmyButton>;
  };

  const IncreaseButton = (props) => {
    const {
      product = passedProduct,
      children,
      size = "lg",
      color = "orange",
      className = "",
      style,
      ...additionalProps
    } = props;
    const hasChildren = children ? children.length > 0 : false;

    return <FarmyButton
      disabled={isLastVariant}
      size={size}
      color={color}
      className={className}
      style={style}
      onClick={() => onIncreaseClick(product)}
      {...additionalProps}
    >
      {hasChildren ? children : "+"}
    </FarmyButton>;
  };

  return {AddToCartButton, IncreaseButton, DecreaseButton, RemoveButton, lineItem, parsedQuantity, isLastVariant};
};

export default useCartButtons;
